import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { publicSiteUrl } from '../core/public-url';
import { DsCardDirective } from '../ui';
import {
  LEGAL_BASE_PATH,
  LEGAL_LAST_UPDATED,
  LEGAL_PAGE_MAP,
  LEGAL_PAGES,
  LegalPage,
  legalUrl,
} from './legal-pages';

@Component({
  selector: 'app-legal-page',
  imports: [RouterLink, DsCardDirective],
  templateUrl: './legal-page.html',
  styleUrl: '../content/content-page.scss',
})
export class LegalPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly pages = LEGAL_PAGES;
  protected readonly page = signal<LegalPage | null>(null);
  protected readonly isIndex = signal(false);
  protected readonly basePath = LEGAL_BASE_PATH;
  protected readonly lastUpdated = LEGAL_LAST_UPDATED;

  constructor() {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const slug = data['legalSlug'] as string | undefined;
      const current = slug ? (LEGAL_PAGE_MAP.get(slug) ?? null) : null;
      this.page.set(current);
      this.isIndex.set(!slug);
      this.applySeo(current);
    });
  }

  protected urlFor(slug: string): string {
    return legalUrl(slug);
  }

  protected pagePath(): string {
    const page = this.page();
    return page ? legalUrl(page.slug) : LEGAL_BASE_PATH;
  }

  protected relatedPages(page: LegalPage): LegalPage[] {
    return page.related
      .map((slug) => LEGAL_PAGE_MAP.get(slug))
      .filter((candidate): candidate is LegalPage => candidate !== undefined);
  }

  private applySeo(page: LegalPage | null): void {
    const title = page ? `${page.title} | IQLabTest` : 'Legal center | IQLabTest';
    const description =
      page?.description ??
      'IQLabTest policies for privacy, cookies, purchases, digital content, certificates, and data retention.';
    const path = page ? legalUrl(page.slug) : LEGAL_BASE_PATH;
    const canonical = publicSiteUrl(path);

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.document.documentElement.lang = 'en';
    this.upsertCanonical(canonical);
    this.upsertStructuredData(page, canonical);
  }

  private upsertCanonical(url: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = url;
  }

  private upsertStructuredData(page: LegalPage | null, canonical: string): void {
    this.document.getElementById('legal-structured-data')?.remove();
    const script = this.document.createElement('script');
    script.id = 'legal-structured-data';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': page ? 'WebPage' : 'CollectionPage',
      name: page?.title ?? 'IQLabTest legal center',
      description: page?.description,
      url: canonical,
      inLanguage: 'en',
      dateModified: LEGAL_LAST_UPDATED,
      publisher: { '@type': 'Organization', name: 'IQLabTest', url: publicSiteUrl() },
    });
    this.document.head.appendChild(script);
  }
}
